import { Clock, AlertTriangle, LogOut } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Modal centrado bloqueante que avisa al usuario que su sesión expirará pronto.
 * Permite extender la sesión o cerrarla manualmente.
 */
export default function IdleTimeoutModal({ open, secondsRemaining, idleTimeoutMinutes, role, onExtend, onLogout }) {
  if (!open) return null;

  const seconds = Math.max(0, secondsRemaining);
  const progress = Math.min(100, Math.max(0, (seconds / 60) * 100));

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="idle-timeout-modal"
      role="dialog"
      aria-labelledby="idle-modal-title"
      aria-modal="true"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden animate-in fade-in zoom-in-95">
        {/* Header amarillo de aviso */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <AlertTriangle size={22} />
          </div>
          <div>
            <h2 id="idle-modal-title" className="text-lg font-extrabold leading-tight">
              Tu sesión está por expirar
            </h2>
            <p className="text-xs text-amber-50">Detectamos inactividad reciente</p>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="px-6 py-5 space-y-4">
          {/* Countdown */}
          <div className="text-center" data-testid="idle-modal-countdown">
            <div className="flex items-center justify-center gap-2 text-slate-600 text-xs uppercase tracking-wider mb-2">
              <Clock size={14} />
              <span>Cierre automático en</span>
            </div>
            <div className="text-5xl font-black text-amber-600 tabular-nums leading-none">
              {seconds}
              <span className="text-2xl text-slate-400 ml-1">s</span>
            </div>
          </div>

          {/* Barra de progreso */}
          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="text-sm text-slate-700 leading-relaxed">
            Por motivos de seguridad, las sesiones inactivas se cierran automáticamente.
            {role && idleTimeoutMinutes && (
              <span className="block text-xs text-slate-500 mt-1">
                Política: <strong>{idleTimeoutMinutes} min</strong> sin actividad para tu rol ({role}).
              </span>
            )}
          </p>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-slate-300"
              onClick={onLogout}
              data-testid="idle-modal-logout"
            >
              <LogOut size={16} className="mr-1" /> Cerrar sesión
            </Button>
            <Button
              type="button"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={onExtend}
              data-testid="idle-modal-extend"
            >
              Mantener sesión
            </Button>
          </div>

          <p className="text-[10px] text-slate-400 text-center pt-1 border-t border-slate-100">
            Cumplimiento NIST SP 800-53 AC-12 · OWASP ASVS V3.3 · PCI-DSS 8.1.8
          </p>
        </div>
      </div>
    </div>
  );
}
